import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { HttpError } from 'app/helpers/httpError';
import { HttpRequestHelper } from 'app/helpers/httpRequestHelper';
import { ExposedListJiraIssuesReq } from 'app/interfaces/requests/Exposed/exposedListJiraIssuesReq';
import { ExposedJiraAllowedValuesFieldsDTORes } from 'app/interfaces/responses/Exposed/exposedJiraAllowedValuesFieldsDTORes';
import { ExposedJiraSearchIssuesDTORes } from 'app/interfaces/responses/Exposed/exposedJiraSearchIssuesDTORes';
import { ExposedListDWAssistsJiraStsRes } from 'app/interfaces/responses/Exposed/exposedListDWAssistsJiraStsRes';
import { JiraCreateIssueDTOReq } from 'app/interfaces/support/jira/jiraCreateIssueDTOReq';
import { HttpStatus } from 'app/interfaces/support/status/httpStatus';
import { AlertsService } from 'app/services/system/alerts/alerts.service';
import { LoaderService } from 'app/services/system/loader/loader.service';
import { environment } from 'environments/environment';
import { Observable, Subject } from 'rxjs';
import { TranslationService } from '../translation/translation.service';

@Injectable({
    providedIn: 'root',
})
export class JiraService {
    private httpRequestHelper = new HttpRequestHelper();

    constructor(
        private http: HttpClient,
        private loaderService: LoaderService,
        private alertsService: AlertsService,
        private router: Router,
        public dialog: MatDialog,
        private translationService: TranslationService
    ) { }

    public createAtvik(req: JiraCreateIssueDTOReq, files: File[] = [], showError = true): Observable<string> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage('Skrái atvik');

        const subject = new Subject<string>();
        const formData = new FormData();

        // Serializing fields as JSON
        if (req.fields != null) {
            formData.append('fields', JSON.stringify(req.fields));
        }

        // Insert all documents into the form
        for (const file of files) {
            formData.append('attachments', file, file.name);
        }

        this.http
            .post(
                environment.localApi + 'jira/project/Atvik/CreateIssue',
                formData,
                this.httpRequestHelper.getRequestOptionsAuthFileUpload()
            )
            .subscribe(
                (res: HttpStatus<string>) => {
                    subject.next(res.returnItem);
                    this.alertsService.changeAlertShowIcon(true);
                    this.alertsService.changeAlertSuccessMessage('Atvik #' + res.returnItem + ' skráð');
                    this.alertsService.changeAlertSuccessShow(true);
                },
                (error) => {
                    subject.next(null);
                    if (showError) {
                        HttpError(this.alertsService, this.router, error, '', '', '', '');
                    } else {
                        console.log(error);
                    }
                }
            )
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });

        return subject.asObservable();
    }

    public listAtvikAllowedValues(loaderMessage = 'Hleð formi', showError = true): Observable<ExposedJiraAllowedValuesFieldsDTORes> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage(loaderMessage);

        let subject = new Subject<ExposedJiraAllowedValuesFieldsDTORes>();
        this.http
            .get(environment.localApi + 'jira/project/Atvik/AllowedValues', this.httpRequestHelper.getRequestOptionsAuth())
            .subscribe(
                (res: HttpStatus<ExposedJiraAllowedValuesFieldsDTORes>) => {
                    subject.next(res.returnItem);
                },
                (error) => {
                    subject.next(null);
                    if (showError) {
                        HttpError(this.alertsService, this.router, error, '', '', '', '');
                    } else {
                        console.log(error);
                    }
                }
            )
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });
        return subject.asObservable();
    }

    public listUtAllowedValues(loaderMessage = 'Hleð formi', showError = true): Observable<ExposedJiraAllowedValuesFieldsDTORes> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage(loaderMessage);

        let subject = new Subject<ExposedJiraAllowedValuesFieldsDTORes>();
        this.http
            .get(environment.localApi + 'jira/project/UT/AllowedValues', this.httpRequestHelper.getRequestOptionsAuth())
            .subscribe(
                (res: HttpStatus<ExposedJiraAllowedValuesFieldsDTORes>) => {
                    subject.next(res.returnItem);
                },
                (error) => {
                    subject.next(null);
                    if (showError) {
                        HttpError(this.alertsService, this.router, error, '', '', '', '');
                    } else {
                        console.log(error);
                    }
                }
            )
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });
        return subject.asObservable();
    }

    public listIssues(req: ExposedListJiraIssuesReq, showError = true): Observable<ExposedJiraSearchIssuesDTORes> {
        const subject = new Subject<ExposedJiraSearchIssuesDTORes>();
        let params = new HttpParams().set('PageSize', String(req.PageSize));
        if (req.NextPageToken) params = params.set('NextPageToken', req.NextPageToken);
        if (req.Ssn) params = params.set('Ssn', req.Ssn);
        if (req.EnsureKey) params = params.set('EnsureKey', req.EnsureKey);
        if (req.EaViewMode !== undefined && req.EaViewMode !== null) params = params.set('EaViewMode', req.EaViewMode);

        const url = `${environment.localApi}jira/projects/${encodeURIComponent(req.Project)}/issues`;

        this.http
            .get<HttpStatus<ExposedJiraSearchIssuesDTORes>>(url, {
                params,
                ...this.httpRequestHelper.getRequestOptionsAuth(),
            })
            .subscribe({
                next: (res) => subject.next(res.returnItem),
                error: (error) => {
                    subject.next(null as any);
                    if (showError) {
                        HttpError(this.alertsService, this.router, error, '', '', '', '');
                    } else {
                        console.log(error);
                    }
                },
            });

        return subject.asObservable();
    }

    public createBVH(req: JiraCreateIssueDTOReq, showError = true): Observable<string> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage('Skrái beiðni');

        const subject = new Subject<string>();
        const formData = new FormData();

        if (req.fields != null) {
            formData.append('fields', JSON.stringify(req.fields));
        }

        this.http
            .post(environment.localApi + 'jira/project/BVH/CreateIssue', formData, this.httpRequestHelper.getRequestOptionsAuthFileUpload())
            .subscribe(
                (res: HttpStatus<string>) => {
                    subject.next(res.returnItem);
                    this.alertsService.changeAlertShowIcon(true);
                    this.alertsService.changeAlertSuccessMessage('Beiðni #' + res.returnItem + ' skráð');
                    this.alertsService.changeAlertSuccessShow(true);
                },
                (error) => {
                    subject.next(null);
                    if (showError) {
                        HttpError(this.alertsService, this.router, error, '', '', '', '');
                    } else {
                        console.log(error);
                    }
                }
            )
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });

        return subject.asObservable();
    }

    public createUT(req: JiraCreateIssueDTOReq, files: File[] = [], showError = true): Observable<string> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage('Skrái beiðni');

        const subject = new Subject<string>();
        const formData = new FormData();

        if (req.fields != null) {
            formData.append('fields', JSON.stringify(req.fields));
        }

        for (const file of files) {
            formData.append('attachments', file, file.name);
        }

        this.http
            .post(environment.localApi + 'jira/project/UT/CreateIssue', formData, this.httpRequestHelper.getRequestOptionsAuthFileUpload())
            .subscribe(
                (res: HttpStatus<string>) => {
                    subject.next(res.returnItem);
                    this.alertsService.changeAlertShowIcon(true);
                    this.alertsService.changeAlertSuccessMessage('Beiðni #' + res.returnItem + ' skráð');
                    this.alertsService.changeAlertSuccessShow(true);
                },
                (error) => {
                    subject.next(null);
                    if (showError) {
                        HttpError(this.alertsService, this.router, error, '', '', '', '');
                    } else {
                        console.log(error);
                    }
                }
            )
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });

        return subject.asObservable();
    }

    public createSTS(req: JiraCreateIssueDTOReq, files: File[] = [], showError = true): Observable<string> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage('Skrái beiðni');

        const subject = new Subject<string>();
        const formData = new FormData();

        if (req.fields != null) {
            formData.append('fields', JSON.stringify(req.fields));
        }

        for (const file of files) {
            formData.append('attachments', file, file.name);
        }

        this.http
            .post(environment.localApi + 'jira/project/STS/CreateIssue', formData, this.httpRequestHelper.getRequestOptionsAuthFileUpload())
            .subscribe(
                (res: HttpStatus<string>) => {
                    subject.next(res.returnItem);
                    this.alertsService.changeAlertShowIcon(true);
                    this.alertsService.changeAlertSuccessMessage('Beiðni #' + res.returnItem + ' skráð');
                    this.alertsService.changeAlertSuccessShow(true);
                },
                (error) => {
                    subject.next(null);
                    if (showError) {
                        HttpError(this.alertsService, this.router, error, '', '', '', '');
                    } else {
                        console.log(error);
                    }
                }
            )
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });

        return subject.asObservable();
    }

    public getGrantsEmployees(showError = true) {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage(this.translationService.translate('info_fetching_information'));

        let subject = new Subject<ExposedListDWAssistsJiraStsRes[]>();
        this.http
            .get(environment.localApi + 'jira/project/STS/GetGrantsEmployees', this.httpRequestHelper.getRequestOptionsAuth())
            .subscribe(
                (res: HttpStatus<ExposedListDWAssistsJiraStsRes[]>) => {
                    subject.next(res.returnItem);
                },
                (error) => {
                    subject.next(null);
                    if (showError) {
                        HttpError(this.alertsService, this.router, error, '', '', '', '');
                    } else {
                        console.log(error);
                    }
                }
            )
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });
        return subject.asObservable();
    }
}
